import React from 'react';
import {
  Alert,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
} from '@patternfly/react-core';
import { formatDate } from '../../../utils/dateFormat';
import type { ResourceStepProps } from './resourceStep';

/**
 * Domain-registration step: review only.
 *
 * The registration is one record, so selecting the type in step 1 selects it whole. Only an
 * **unmanaged** record (one Ancalagon does not hold at a registrar) can be removed: the registrar
 * drivers have no cancel or transfer-out verb, so deleting the local row of a live registration would
 * orphan it. The backend also requires the DNS zone to go first, which this step says out loud rather
 * than letting the run be refused. `docs/api-types/domain-names.md` → Delete Domain Name.
 */
const DomainRegistrationStep: React.FunctionComponent<ResourceStepProps> = ({ domain, resources, elements }) => {
  const registration = resources.domain_registration;
  const element = elements[0] ?? null;

  if (registration === null || element === null) {
    return (
      <Content component="p">
        Ancalagon holds no registration record for {domain.name}, so nothing is removed here.
      </Content>
    );
  }

  const registrar = registration.domain_registration_platform?.name ?? null;
  const contact = registration.registrant_contact;
  const zoneStillBound = resources.dns_zone !== null;

  return (
    <div>
      <Content component="p" className="pf-v6-u-mb-md">
        This is the registration record Ancalagon keeps for <strong>{domain.name}</strong>.
      </Content>

      <DescriptionList isHorizontal isCompact aria-label={`Registration record of ${domain.name}`}>
        <DescriptionListGroup>
          <DescriptionListTerm>Registrar</DescriptionListTerm>
          <DescriptionListDescription>
            {registrar ?? 'Not registered through Ancalagon (tracked record only)'}
          </DescriptionListDescription>
        </DescriptionListGroup>
        {contact !== null && (
          <DescriptionListGroup>
            <DescriptionListTerm>Registrant</DescriptionListTerm>
            <DescriptionListDescription>
              {[contact.first_name, contact.last_name].filter((part) => part !== '').join(' ')}
              {contact.organization !== null ? ` — ${contact.organization}` : ''}
            </DescriptionListDescription>
          </DescriptionListGroup>
        )}
        <DescriptionListGroup>
          <DescriptionListTerm>Expires</DescriptionListTerm>
          <DescriptionListDescription>{formatDate(registration.expires_at)}</DescriptionListDescription>
        </DescriptionListGroup>
        {registration.vanishes_at !== null && (
          <DescriptionListGroup>
            <DescriptionListTerm>Leaves the registry</DescriptionListTerm>
            <DescriptionListDescription>{formatDate(registration.vanishes_at)}</DescriptionListDescription>
          </DescriptionListGroup>
        )}
        <DescriptionListGroup>
          <DescriptionListTerm>Renewal billing</DescriptionListTerm>
          <DescriptionListDescription>
            {registration.next_billing_at === null
              ? 'Not enrolled'
              : `Next charge ${formatDate(registration.next_billing_at)}`}
          </DescriptionListDescription>
        </DescriptionListGroup>
        {registration.in_transfer && (
          <DescriptionListGroup>
            <DescriptionListTerm>Transfer</DescriptionListTerm>
            <DescriptionListDescription>
              In progress
              {registration.transfer_expires_at !== null
                ? ` until ${formatDate(registration.transfer_expires_at)}`
                : ''}
            </DescriptionListDescription>
          </DescriptionListGroup>
        )}
      </DescriptionList>

      {element.deletable ? (
        <>
          <Alert
            variant="warning"
            isInline
            component="h2"
            title="The record and its registrant contact are deleted"
            className="pf-v6-u-mt-md"
          >
            <p>
              Ancalagon stops tracking {domain.name} and stops charging its renewal. Nothing is cancelled at a
              registrar, because this record is not held at one. Order history is kept.
            </p>
          </Alert>
          {zoneStillBound && (
            <Alert
              variant="info"
              isInline
              component="h2"
              title="The DNS zone has to go in the same run"
              className="pf-v6-u-mt-md"
            >
              <p>
                This domain is still bound to Ancalagon&apos;s nameservers. Select the DNS zone as well, or the record
                cannot be removed.
              </p>
            </Alert>
          )}
        </>
      ) : (
        <Alert variant="warning" isInline component="h2" title="This record is kept" className="pf-v6-u-mt-md">
          <p>{element.blockedReason ?? 'This registration record cannot be deleted right now.'}</p>
        </Alert>
      )}
    </div>
  );
};

export default DomainRegistrationStep;
